import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock3,
  Copy,
  GitCompareArrows,
  History,
  Loader2,
  Play,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@design-system';
import {
  summarizeCalcSnapshotDiff,
  type CalcSnapshot,
  type CalcSnapshotImpact,
  type SnapshotFieldDiff,
} from '@shared/calculations/snapshot-testing';
import {
  useReviseWizardCalcSnapshotTestCase,
  useRunWizardCalcSnapshots,
  useWizardCalcSnapshotDetail,
  useWizardCalcSnapshotRuns,
} from '../hooks/use-wizard-calc-snapshots';
import type {
  WizardCalcSnapshotRun,
  WizardCalcSnapshotRunResult,
  WizardCalcSnapshotRunResultSummary,
} from '../api/wizard-calc-snapshots-api';
import {
  CALC_SNAPSHOT_RUN_ERROR_MESSAGE,
  CalcSnapshotMetricCard,
  ResultBadge,
  buildCalcSnapshotCommitRange,
  formatCalcSnapshotGitRevision,
  formatCalcSnapshotSourceFieldLabel,
  formatCalcSnapshotSourceValue,
  formatSnapshotCurrency,
  formatSnapshotDateTime,
  getCalcSnapshotErrorMessage,
  parseCalcSnapshotGitRevision,
} from './CalcSnapshotUi';

interface CalcSnapshotDetailSheetProps {
  testCaseId: string | null;
  runResult: WizardCalcSnapshotRunResult | null;
  historicalRunResultId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function CalcSnapshotDetailSheet({
  testCaseId,
  runResult,
  historicalRunResultId,
  onOpenChange,
}: CalcSnapshotDetailSheetProps) {
  const runsQuery = useWizardCalcSnapshotRuns();
  const scenarioRuns = (runsQuery.data ?? []).flatMap(run =>
    run.results.filter(result => result.testCaseId === testCaseId).map(result => ({ run, result })),
  );
  const requestedRunResultId = runResult?.runResultId ?? historicalRunResultId;
  const latestRunResultId = requestedRunResultId
    ? null
    : (scenarioRuns[0]?.result.runResultId ?? null);
  const detailQuery = useWizardCalcSnapshotDetail(
    testCaseId,
    requestedRunResultId ?? latestRunResultId,
  );
  const runMutation = useRunWizardCalcSnapshots();
  const [selectedTab, setSelectedTab] = useState('overview');
  const [activeResult, setActiveResult] = useState<WizardCalcSnapshotRunResult | null>(runResult);
  const [revisionMode, setRevisionMode] = useState<'bless' | 'repin' | null>(null);
  const detail = detailQuery.data;
  const activeRunEntry = activeResult
    ? scenarioRuns.find(entry => entry.result.runResultId === activeResult.runResultId)
    : undefined;
  const previousPassingRunEntry = findPreviousCleanRun(scenarioRuns, activeRunEntry);
  const latestRunEntry = scenarioRuns[0] ?? null;
  const previousPassingLatestRunEntry = findPreviousCleanRun(
    scenarioRuns,
    latestRunEntry ?? undefined,
  );
  const activeImpacts = activeResult
    ? (activeResult.diff.impactGroups ?? summarizeCalcSnapshotDiff(activeResult.diff))
    : [];
  const activeWarningCount = activeImpacts.filter(impact => impact.severity === 'warning').length;

  useEffect(() => {
    setActiveResult(runResult);
    setSelectedTab(requestedRunResultId ? 'comparison' : 'overview');
  }, [requestedRunResultId, runResult, testCaseId]);

  useEffect(() => {
    if (!runResult && (historicalRunResultId || latestRunResultId) && detail?.runResult) {
      setActiveResult(detail.runResult);
    }
  }, [detail?.runResult, historicalRunResultId, latestRunResultId, runResult]);

  const runTest = () => {
    if (!testCaseId) return;
    runMutation.mutate(
      { scope: 'single', testCaseId },
      {
        onSuccess: response => {
          setActiveResult(
            response.results.find(result => result.testCaseId === testCaseId) ?? null,
          );
          setSelectedTab('comparison');
        },
      },
    );
  };

  return (
    <>
      <Sheet
        open={testCaseId != null}
        onOpenChange={onOpenChange}
      >
        <SheetContent className="w-full gap-0 overflow-hidden p-0 sm:w-[85vw] sm:max-w-[1080px]">
          <SheetHeader className="border-b px-6 py-5 pr-14 text-left">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  {detail?.testCase && <TestCaseStatusBadges testCase={detail.testCase} />}
                </div>
                <SheetTitle className="text-xl leading-7">
                  {detail?.testCase.name ?? 'Calculation test case'}
                </SheetTitle>
                <SheetDescription className="mt-1 max-w-2xl">
                  {detail?.testCase.description || 'No description'}
                </SheetDescription>
              </div>
              <div
                className={`shrink-0 flex-wrap gap-2 pr-4 ${selectedTab === 'run-history' || historicalRunResultId ? 'hidden' : 'flex'}`}
              >
                <Button
                  variant="outline"
                  disabled={
                    selectedTab !== 'overview' ||
                    !detail?.divergence.hasDiverged ||
                    runMutation.isPending
                  }
                  onClick={() => setRevisionMode('repin')}
                >
                  <RefreshCw className="size-4" />
                  Update frozen inputs
                </Button>
                <Button
                  variant="outline"
                  disabled={
                    selectedTab !== 'comparison' ||
                    !activeResult ||
                    activeResult.passed ||
                    activeResult.testCaseRevision !== detail?.testCase.revision ||
                    runMutation.isPending
                  }
                  onClick={() => setRevisionMode('bless')}
                >
                  <ShieldCheck className="size-4" />
                  Accept current results
                </Button>
                <Button
                  disabled={!detail || runMutation.isPending}
                  onClick={runTest}
                >
                  {runMutation.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Play className="size-4" />
                  )}
                  Run test
                </Button>
              </div>
            </div>
          </SheetHeader>

          {runMutation.error && (
            <div className="px-6 pt-4">
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>Unable to run calculation tests</AlertTitle>
                <AlertDescription>{CALC_SNAPSHOT_RUN_ERROR_MESSAGE}</AlertDescription>
              </Alert>
            </div>
          )}

          {detailQuery.isLoading ? (
            <div className="text-muted-foreground flex flex-1 items-center justify-center gap-2">
              <Loader2 className="size-5 animate-spin" />
              Loading test case…
            </div>
          ) : detailQuery.error ? (
            <div className="p-6">
              <Alert variant="destructive">
                <XCircle />
                <AlertTitle>Unable to load test case</AlertTitle>
                <AlertDescription>
                  {getCalcSnapshotErrorMessage(detailQuery.error)}
                </AlertDescription>
              </Alert>
            </div>
          ) : detail ? (
            <Tabs
              value={selectedTab}
              onValueChange={setSelectedTab}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="border-b px-6 py-4">
                <TabsList className="bg-primary-100 h-12 max-w-full justify-start overflow-x-auto p-1">
                  <TabsTrigger
                    value="overview"
                    className="h-full px-5 text-base font-semibold"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger
                    value="comparison"
                    className="h-full px-5 text-base font-semibold"
                  >
                    Comparison
                    {activeResult && (
                      <Badge
                        variant={
                          activeResult.passed
                            ? activeWarningCount
                              ? 'warning'
                              : 'success'
                            : 'destructive'
                        }
                        className="ml-2"
                      >
                        {activeResult.passed
                          ? activeWarningCount
                            ? 'Passed with warnings'
                            : 'Passed'
                          : 'Failed'}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="run-history"
                    className="h-full px-5 text-base font-semibold"
                  >
                    Run History
                    <Badge
                      variant="secondary"
                      className="ml-2"
                    >
                      {scenarioRuns.length}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="revisions"
                    className="h-full px-5 text-base font-semibold"
                  >
                    Revisions
                    <Badge
                      variant="secondary"
                      className="ml-2"
                    >
                      {detail.revisions.length}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent
                value="overview"
                className="min-h-0 flex-1 overflow-y-auto p-6"
              >
                <OverviewPanel
                  detail={detail}
                  latestRunEntry={latestRunEntry}
                  previousPassingRun={previousPassingLatestRunEntry?.run ?? null}
                  onViewComparison={() => setSelectedTab('comparison')}
                />
              </TabsContent>
              <TabsContent
                value="comparison"
                className="min-h-0 flex-1 overflow-y-auto p-6"
              >
                <CalcSnapshotComparisonPanel
                  result={activeResult}
                  expectedSnapshot={detail.runExpectedSnapshot ?? detail.testCase.expectedSnapshot}
                  testCase={detail.testCase}
                  currentRun={activeRunEntry?.run ?? null}
                  previousPassingRun={previousPassingRunEntry?.run ?? null}
                  context={historicalRunResultId ? 'historical' : 'latest'}
                  isRunning={runMutation.isPending}
                  onRun={runTest}
                />
              </TabsContent>
              <TabsContent
                value="run-history"
                className="min-h-0 flex-1 overflow-y-auto p-6"
              >
                <ScenarioRunHistory
                  entries={scenarioRuns}
                  testCaseId={testCaseId}
                  isLoading={runsQuery.isLoading}
                  error={runsQuery.error}
                />
              </TabsContent>
              <TabsContent
                value="revisions"
                className="min-h-0 flex-1 overflow-y-auto p-6"
              >
                <RevisionHistory revisions={detail.revisions} />
              </TabsContent>
            </Tabs>
          ) : null}
        </SheetContent>
      </Sheet>

      <RevisionActionDialog
        mode={revisionMode}
        testCaseId={testCaseId}
        onOpenChange={open => !open && setRevisionMode(null)}
        onRevised={() => {
          setRevisionMode(null);
          setActiveResult(null);
          setSelectedTab('overview');
        }}
      />
    </>
  );
}

function OverviewPanel({
  detail,
  latestRunEntry,
  previousPassingRun,
  onViewComparison,
}: {
  detail: NonNullable<ReturnType<typeof useWizardCalcSnapshotDetail>['data']>;
  latestRunEntry: ScenarioRunEntry | null;
  previousPassingRun: WizardCalcSnapshotRun | null;
  onViewComparison: () => void;
}) {
  const { testCase, divergence } = detail;
  const ledger = testCase.expectedSnapshot.ledgerTotals;
  const policy = testCase.expectedSnapshot.policyTotals;

  return (
    <div className="space-y-6">
      <Alert variant={divergence.hasDiverged ? 'warning' : 'success'}>
        {divergence.hasDiverged ? <AlertTriangle /> : <CheckCircle2 />}
        <AlertTitle>
          {divergence.hasDiverged
            ? 'Live client differs from the pinned bundle'
            : 'Live client matches the pinned bundle'}
        </AlertTitle>
        <AlertDescription>
          {divergence.error ? (
            <p>{divergence.error}</p>
          ) : divergence.hasDiverged ? (
            <p>
              {divergence.fields.length} source field changes detected. Update the frozen inputs
              only after review. The test still runs against its frozen inputs.
            </p>
          ) : (
            <p>The editable source data has not changed since this test case was pinned.</p>
          )}
        </AlertDescription>
      </Alert>

      {divergence.hasDiverged && divergence.fields.length > 0 && (
        <DiffTable
          title="Source changes"
          diffs={divergence.fields}
        />
      )}

      <section>
        <SectionTitle title="Latest run" />
        <Card className="gap-0 py-0 shadow-none">
          <CardContent className="space-y-5 p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {latestRunEntry ? (
                <div>
                  <ResultBadge result={latestRunEntry.result} />
                  <p className="text-muted-foreground mt-2 text-sm">
                    {formatSnapshotDateTime(
                      latestRunEntry.run.completedAt ?? latestRunEntry.run.createdDateTime,
                    )}{' '}
                    · Revision {latestRunEntry.result.testCaseRevision}
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  This test case has not been run yet.
                </p>
              )}
              <Button
                variant="outline"
                disabled={!latestRunEntry}
                onClick={onViewComparison}
              >
                <GitCompareArrows className="size-4" />
                View comparison
              </Button>
            </div>
            {latestRunEntry && (
              <CommitContext
                resultPassed={latestRunEntry.result.passed}
                currentRun={latestRunEntry.run}
                previousPassingRun={previousPassingRun}
              />
            )}
          </CardContent>
        </Card>
      </section>

      <section>
        <SectionTitle title="Pinned snapshot" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <CalcSnapshotMetricCard
            label="Total payout"
            value={formatSnapshotCurrency(ledger.totalPayout)}
          />
          <CalcSnapshotMetricCard
            label="Projected care cost"
            value={formatSnapshotCurrency(ledger.totalProjectedCareCost)}
          />
          <CalcSnapshotMetricCard
            label="Out-of-pocket"
            value={formatSnapshotCurrency(ledger.totalOutOfPocketCost)}
          />
          <CalcSnapshotMetricCard
            label="Policy total cost"
            value={formatSnapshotCurrency(policy.policyTotalCost)}
          />
        </div>
      </section>

      <section>
        <SectionTitle title="Scenario coverage" />
        <div className="flex flex-wrap gap-2">
          <ScenarioBadges facets={testCase.facets} />
        </div>
      </section>

      <section>
        <SectionTitle title="Snapshot details" />
        <Card className="gap-0 py-0 shadow-none">
          <CardContent className="grid gap-5 p-5 sm:grid-cols-2">
            <DetailItem
              icon={<CalendarDays />}
              label="Frozen as of"
              value={testCase.asOfDate}
            />
            <DetailItem
              icon={<History />}
              label="Revision"
              value={`Revision ${testCase.revision}`}
            />
            <DetailItem
              icon={<ShieldCheck />}
              label="Policy"
              value={`${detail.policySummary.primaryLabel} · ${detail.policySummary.secondaryLabel}`}
            />
            <DetailItem
              icon={<GitCompareArrows />}
              label="Calculation strategy"
              value={testCase.strategyId}
            />
            <DetailItem
              icon={<Clock3 />}
              label="Pinned by"
              value={`${testCase.createdBy} · ${formatSnapshotDateTime(testCase.createdDateTime)}`}
            />
          </CardContent>
        </Card>
      </section>

      {testCase.tags.length > 0 && (
        <section>
          <SectionTitle title="Tags" />
          <div className="flex flex-wrap gap-2">
            {testCase.tags.map(tag => (
              <Badge
                key={tag}
                variant="secondary"
              >
                {tag}
              </Badge>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export function CalcSnapshotComparisonPanel({
  result,
  expectedSnapshot,
  testCase,
  currentRun,
  previousPassingRun,
  context,
  isRunning,
  onRun,
}: {
  result: WizardCalcSnapshotRunResult | null;
  expectedSnapshot: CalcSnapshot | null;
  testCase: NonNullable<ReturnType<typeof useWizardCalcSnapshotDetail>['data']>['testCase'];
  currentRun: WizardCalcSnapshotRun | null;
  previousPassingRun: WizardCalcSnapshotRun | null;
  context: 'latest' | 'historical';
  isRunning: boolean;
  onRun: () => void;
}) {
  if (!result) {
    return (
      <div className="flex min-h-72 flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center">
        <GitCompareArrows className="text-muted-foreground mb-3 size-8" />
        <h3 className="font-semibold text-gray-950">No comparison yet</h3>
        <p className="text-muted-foreground mt-1 max-w-md text-sm">
          Run this test case to calculate the current output and compare it with the pinned
          snapshot.
        </p>
        <Button
          className="mt-5"
          disabled={isRunning}
          onClick={onRun}
        >
          {isRunning ? <Loader2 className="size-4 animate-spin" /> : <Play className="size-4" />}
          Run test
        </Button>
      </div>
    );
  }

  const diff = result.diff;
  const impacts = diff.impactGroups ?? summarizeCalcSnapshotDiff(diff);
  const warningCount = impacts.filter(impact => impact.severity === 'warning').length;

  return (
    <div className="space-y-6">
      {context === 'historical' && currentRun && (
        <div className="rounded-xl border bg-gray-50 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary">Historical · Read-only</Badge>
            <span className="text-sm font-medium text-gray-950">
              {formatSnapshotDateTime(currentRun.completedAt ?? currentRun.createdDateTime)}
            </span>
          </div>
          <p className="text-muted-foreground mt-2 text-sm">
            Compared with the accepted Revision {result.testCaseRevision} baseline that was active
            for this run.
          </p>
        </div>
      )}

      <Alert variant={result.passed ? (warningCount ? 'warning' : 'success') : 'destructive'}>
        {result.passed ? <CheckCircle2 /> : <XCircle />}
        <AlertTitle>
          {result.passed
            ? warningCount
              ? 'Passed with warnings'
              : context === 'historical'
                ? 'This run matches its pinned snapshot'
                : 'Current calculation matches the pinned snapshot'
            : 'Calculation regression detected'}
        </AlertTitle>
        <AlertDescription>
          {result.passed && warningCount ? (
            <p>The existing calculation results match. Newly tracked results require review.</p>
          ) : result.passed ? (
            <p>No calculation differences were found.</p>
          ) : (
            <p>
              The calculation no longer matches the pinned snapshot.
              {result.firstDivergentMonthIndex != null
                ? ` The first changed projection month is ${result.firstDivergentMonthIndex + 1}.`
                : ''}
            </p>
          )}
        </AlertDescription>
      </Alert>

      {impacts.length > 0 && (
        <InvestigationSummary
          result={result}
          expectedSnapshot={expectedSnapshot}
          testCase={testCase}
          currentRun={currentRun}
          previousPassingRun={previousPassingRun}
          impacts={impacts}
        />
      )}

      {impacts.length === 0 && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
          <CheckCircle2 className="mx-auto size-8 text-green-700" />
          <p className="mt-3 font-medium text-green-900">No field-level differences found.</p>
        </div>
      )}
    </div>
  );
}

function InvestigationSummary({
  result,
  expectedSnapshot,
  testCase,
  currentRun,
  previousPassingRun,
  impacts,
}: {
  result: WizardCalcSnapshotRunResult;
  expectedSnapshot: CalcSnapshot | null;
  testCase: NonNullable<ReturnType<typeof useWizardCalcSnapshotDetail>['data']>['testCase'];
  currentRun: WizardCalcSnapshotRun | null;
  previousPassingRun: WizardCalcSnapshotRun | null;
  impacts: CalcSnapshotImpact[];
}) {
  const [copied, setCopied] = useState(false);
  const currentRevision = parseCalcSnapshotGitRevision(currentRun?.gitSha ?? null);
  const previousRevision = parseCalcSnapshotGitRevision(previousPassingRun?.gitSha ?? null);
  const commitRange = buildCalcSnapshotCommitRange(previousRevision, currentRevision);
  const resultLabel = result.passed
    ? impacts.some(impact => impact.severity === 'warning')
      ? 'Passed with warnings'
      : 'Passed'
    : 'Failed';
  const primaryImpacts = selectPrimaryImpacts(impacts, result.firstDivergentMonthIndex);
  const hasAdditionalImpacts = impacts.length > primaryImpacts.length;
  const investigationPacket = buildInvestigationPacket({
    result,
    expectedSnapshot,
    testCase,
    currentRun,
    previousPassingRun,
    impacts,
    resultLabel,
    commitRange,
  });

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(investigationPacket);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="rounded-xl border bg-gray-50 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="font-semibold text-gray-950">Investigation summary</h3>
          <p className="text-muted-foreground mt-1 text-sm">
            The commit identifies where this result was observed. It does not by itself identify
            which untested commit caused a regression.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={copySummary}
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? 'Copied' : 'Copy summary'}
        </Button>
      </div>

      {primaryImpacts.length > 0 && (
        <div className="mt-4 rounded-lg border bg-white p-3">
          <p className="text-muted-foreground text-xs font-medium uppercase">
            First observed calculation changes
          </p>
          <ul className="mt-2 space-y-2 text-sm">
            {primaryImpacts.map(impact => (
              <li
                key={impact.key}
                className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-4"
              >
                <span className="font-medium text-gray-950">{impact.label}</span>
                <span className="text-muted-foreground">{formatImpactSummaryValue(impact)}</span>
              </li>
            ))}
          </ul>
          {hasAdditionalImpacts && (
            <p className="text-muted-foreground mt-3 border-t pt-3 text-xs">
              Additional downstream result changes are omitted.
            </p>
          )}
        </div>
      )}

      <CommitContext
        className="mt-4"
        resultPassed={result.passed}
        currentRun={currentRun}
        previousPassingRun={previousPassingRun}
      />
    </section>
  );
}

function buildInvestigationPacket({
  result,
  expectedSnapshot,
  testCase,
  currentRun,
  previousPassingRun,
  impacts,
  resultLabel,
  commitRange,
}: {
  result: WizardCalcSnapshotRunResult;
  expectedSnapshot: CalcSnapshot | null;
  testCase: NonNullable<ReturnType<typeof useWizardCalcSnapshotDetail>['data']>['testCase'];
  currentRun: WizardCalcSnapshotRun | null;
  previousPassingRun: WizardCalcSnapshotRun | null;
  impacts: CalcSnapshotImpact[];
  resultLabel: string;
  commitRange: string | null;
}): string {
  const currentRevision = parseCalcSnapshotGitRevision(currentRun?.gitSha ?? null);
  const previousRevision = parseCalcSnapshotGitRevision(previousPassingRun?.gitSha ?? null);
  const sensitiveValues = [
    testCase.testCaseId,
    testCase.advisorId,
    testCase.sourceClientId,
    testCase.sourcePartnerClientId,
    testCase.fundingSourceId,
    testCase.createdBy,
    currentRun?.advisorId,
    currentRun?.triggeredBy,
    previousPassingRun?.advisorId,
    previousPassingRun?.triggeredBy,
  ].filter((value): value is string => Boolean(value));
  const divergenceWindow = getSnapshotDivergenceWindow(
    expectedSnapshot,
    result.actualSnapshot,
    result.firstDivergentMonthIndex,
  );
  const packet = redactInvestigationData(
    {
      packetVersion: 1,
      repository: 'joinwaterlily/waterlily-ltc-planning-app',
      purpose: 'Identify the calculation code change that produced this snapshot regression.',
      testContext: {
        strategyId: testCase.strategyId,
        policyType: testCase.policyType,
        asOfDate: testCase.asOfDate,
        snapshotRevision: result.testCaseRevision,
        scenarioFacets: testCase.facets,
      },
      runContext: {
        result: resultLabel,
        completedAt: currentRun?.completedAt ?? result.createdDateTime,
        firstDivergentMonthIndex: result.firstDivergentMonthIndex,
        firstDivergentProjectionMonth:
          result.firstDivergentMonthIndex == null ? null : result.firstDivergentMonthIndex + 1,
      },
      gitContext: {
        lastKnownPassingCommit: previousRevision.sha,
        lastKnownPassingCommitHadLocalChanges: previousRevision.dirty,
        resultObservedAtCommit: currentRevision.sha,
        observedCommitHadLocalChanges: currentRevision.dirty,
        commitRange,
        calculationPath: 'packages/shared/src/calculations',
      },
      evidence: {
        acceptedBaseline: snapshotContext(expectedSnapshot),
        currentRun: snapshotContext(result.actualSnapshot),
        divergenceWindow,
        computedDiff: result.diff,
        groupedImpacts: impacts,
      },
      evidenceNotes: [
        'computedDiff contains every changed, added, and removed calculation field with its expected and actual value.',
        'divergenceWindow contains the complete projection rows immediately before, at, and after the first divergence.',
        'Unchanged monthly rows and unchanged flattened projection fields are omitted to keep the packet usable.',
        'Client identity, advisor identity, policy identifiers, names, emails, and free-text labels are excluded or redacted.',
      ],
    },
    sensitiveValues,
  );

  return [
    'Investigate this Waterlily calculation snapshot result.',
    '',
    'Use only the evidence packet below and GitHub MCP access to the named repository. Do not request or use database access. Inspect the supplied commit range when it is available; otherwise explain the missing Git boundary instead of guessing. Start from the first divergent projection month and the earliest direct changes, then separate the likely primary behavioral change from downstream recalculation effects.',
    '',
    'Return:',
    '1. The most likely root cause.',
    '2. The supporting evidence from the packet.',
    '3. The relevant commit, file, and function found through GitHub MCP.',
    '4. Which reported changes are direct effects and which are downstream effects.',
    '5. Confidence and any limitation that prevents a definitive conclusion.',
    '',
    '<calculation_snapshot_investigation_packet>',
    JSON.stringify(packet, null, 2),
    '</calculation_snapshot_investigation_packet>',
  ].join('\n');
}

function snapshotContext(snapshot: CalcSnapshot | null) {
  if (!snapshot) return null;
  return {
    snapshotVersion: snapshot.snapshotVersion,
    asOfDate: snapshot.asOfDate,
    strategyId: snapshot.strategyId,
    terms: snapshot.terms,
    ledgerTotals: snapshot.ledgerTotals,
    policyTotals: snapshot.policyTotals,
    partnerPolicyTotals: snapshot.partnerPolicyTotals ?? null,
    digest: snapshot.digest,
  };
}

function getSnapshotDivergenceWindow(
  expectedSnapshot: CalcSnapshot | null,
  actualSnapshot: CalcSnapshot | null,
  firstDivergentMonthIndex: number | null,
) {
  if (!expectedSnapshot || !actualSnapshot || firstDivergentMonthIndex == null) return null;
  const includedMonthIndexes = new Set([
    Math.max(0, firstDivergentMonthIndex - 1),
    firstDivergentMonthIndex,
    firstDivergentMonthIndex + 1,
  ]);
  const selectRows = (snapshot: CalcSnapshot) =>
    snapshot.rows.filter(row => includedMonthIndexes.has(row.monthIndex));

  return {
    expectedRows: selectRows(expectedSnapshot),
    actualRows: selectRows(actualSnapshot),
  };
}

function redactInvestigationData(value: unknown, sensitiveValues: string[]): unknown {
  if (Array.isArray(value)) {
    return value.map(item => redactInvestigationData(item, sensitiveValues));
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [
        key,
        redactInvestigationData(nestedValue, sensitiveValues),
      ]),
    );
  }
  if (typeof value !== 'string') return value;

  return sensitiveValues.reduce(
    (redacted, sensitiveValue) => redacted.replaceAll(sensitiveValue, '[redacted]'),
    value,
  );
}

function CommitContext({
  resultPassed,
  currentRun,
  previousPassingRun,
  className,
}: {
  resultPassed: boolean;
  currentRun: WizardCalcSnapshotRun | null;
  previousPassingRun: WizardCalcSnapshotRun | null;
  className?: string;
}) {
  const currentRevision = parseCalcSnapshotGitRevision(currentRun?.gitSha ?? null);
  const previousRevision = parseCalcSnapshotGitRevision(previousPassingRun?.gitSha ?? null);
  const commitRange = buildCalcSnapshotCommitRange(previousRevision, currentRevision);

  return (
    <dl className={cn('grid gap-3 border-t pt-4 text-sm sm:grid-cols-3', className)}>
      <div>
        <dt className="text-muted-foreground">Last known passing commit</dt>
        <dd className="mt-1 font-mono font-medium text-gray-950">
          {formatCalcSnapshotGitRevision(previousPassingRun?.gitSha ?? null, 'Not captured')}
        </dd>
      </div>
      <div>
        <dt className="text-muted-foreground">
          {resultPassed ? 'Tested commit' : 'Failure observed at commit'}
        </dt>
        <dd className="mt-1 font-mono font-medium text-gray-950">
          {formatCalcSnapshotGitRevision(currentRun?.gitSha ?? null, 'Not captured')}
        </dd>
      </div>
      <div>
        <dt className="text-muted-foreground">Commit range</dt>
        <dd className="mt-1 font-mono font-medium text-gray-950">
          {commitRange ?? 'Not available'}
        </dd>
      </div>
    </dl>
  );
}

function formatImpactSummaryValue(impact: CalcSnapshotImpact): string {
  return impact.hasSingleValue
    ? `${formatComparisonValue(impact.expected, 'expected')} -> ${formatComparisonValue(impact.actual, 'actual')}`
    : `${impact.differenceCount} related raw differences`;
}

function selectPrimaryImpacts(
  impacts: CalcSnapshotImpact[],
  firstDivergentMonthIndex: number | null,
): CalcSnapshotImpact[] {
  const failures = impacts.filter(impact => impact.severity === 'failure');
  const candidates = failures.length ? failures : impacts;
  const firstMonthCandidates =
    firstDivergentMonthIndex == null
      ? []
      : candidates.filter(
          impact =>
            impact.firstMonthIndex === firstDivergentMonthIndex &&
            impact.lastMonthIndex === firstDivergentMonthIndex,
        );
  const ranked = firstMonthCandidates.length ? firstMonthCandidates : candidates;

  return [...ranked]
    .sort((left, right) => Number(right.hasSingleValue) - Number(left.hasSingleValue))
    .slice(0, 3);
}

function DiffTable({ title, diffs }: { title: string; diffs: SnapshotFieldDiff[] }) {
  return (
    <section className="overflow-hidden rounded-xl border">
      <div className="flex items-center justify-between border-b bg-gray-50 px-4 py-3">
        <h3 className="font-semibold text-gray-950">{title}</h3>
        <Badge variant="secondary">{diffs.length}</Badge>
      </div>
      <div className="overflow-x-auto">
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[42%] pl-4">Field</TableHead>
              <TableHead>Expected</TableHead>
              <TableHead>Actual</TableHead>
              <TableHead className="pr-4 text-right">Change</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {diffs.map((field, index) => (
              <TableRow key={`${field.path}-${field.kind}-${index}`}>
                <TableCell className="max-w-0 whitespace-normal pl-4 font-medium text-gray-900">
                  <span className="break-words">
                    {formatCalcSnapshotSourceFieldLabel(field.path)}
                  </span>
                </TableCell>
                <TableCell className="max-w-48 whitespace-normal text-sm">
                  <span className="break-words">
                    {formatCalcSnapshotSourceValue(field.path, field.expected)}
                  </span>
                </TableCell>
                <TableCell className="max-w-48 whitespace-normal text-sm">
                  <span className="break-words">
                    {formatCalcSnapshotSourceValue(field.path, field.actual)}
                  </span>
                </TableCell>
                <TableCell className="pr-4 text-right">
                  <Badge variant={field.severity === 'failure' ? 'destructive' : 'warning'}>
                    {field.kind}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

interface ScenarioRunEntry {
  run: WizardCalcSnapshotRun;
  result: WizardCalcSnapshotRunResultSummary;
}

function ScenarioRunHistory({
  entries,
  testCaseId,
  isLoading,
  error,
}: {
  entries: ScenarioRunEntry[];
  testCaseId: string | null;
  isLoading: boolean;
  error: unknown;
}) {
  const [expandedRunResultId, setExpandedRunResultId] = useState<string | null>(null);

  useEffect(() => {
    setExpandedRunResultId(null);
  }, [testCaseId]);

  if (isLoading) {
    return (
      <div className="text-muted-foreground flex min-h-48 items-center justify-center gap-2">
        <Loader2 className="size-5 animate-spin" />
        Loading run history…
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <XCircle />
        <AlertTitle>Unable to load run history</AlertTitle>
        <AlertDescription>{getCalcSnapshotErrorMessage(error)}</AlertDescription>
      </Alert>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm">
        This test case has not been run yet.
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border">
      <div className="border-b bg-gray-50 px-4 py-3">
        <h3 className="font-semibold text-gray-950">Runs for this test case</h3>
      </div>
      <div className="text-muted-foreground hidden grid-cols-[1.2fr_0.8fr_0.7fr_0.9fr_1.2fr_auto] gap-3 border-b px-8 py-3 text-xs font-medium md:grid">
        <span>Run date</span>
        <span>Status</span>
        <span>Revision</span>
        <span>Git commit</span>
        <span>Triggered by</span>
        <span className="w-4" />
      </div>
      <Accordion
        type="single"
        collapsible
        value={expandedRunResultId ?? ''}
        onValueChange={value => setExpandedRunResultId(value || null)}
      >
        {entries.map((entry, index) => {
          const { run, result } = entry;
          const previousPassingRun = findPreviousCleanRun(entries, entry)?.run ?? null;
          return (
            <AccordionItem
              key={result.runResultId}
              value={result.runResultId}
              className="border-b px-4 last:border-b-0"
            >
              <AccordionTrigger className="py-4 hover:no-underline">
                <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 pr-4 text-left md:grid-cols-[1.2fr_0.8fr_0.7fr_0.9fr_1.2fr] md:items-center">
                  <div>
                    <p className="font-medium text-gray-950">
                      {formatSnapshotDateTime(run.completedAt ?? run.createdDateTime)}
                    </p>
                    {index === 0 && (
                      <p className="text-muted-foreground mt-1 text-xs">Latest run</p>
                    )}
                  </div>
                  <div>
                    <ResultBadge result={result} />
                  </div>
                  <p className="text-sm">Revision {result.testCaseRevision}</p>
                  <p className="font-mono text-xs">{formatCalcSnapshotGitRevision(run.gitSha)}</p>
                  <p className="text-muted-foreground truncate text-sm">{run.triggeredBy}</p>
                </div>
              </AccordionTrigger>
              <AccordionContent className="border-t py-5">
                {expandedRunResultId === result.runResultId && testCaseId && (
                  <HistoricalRunResult
                    testCaseId={testCaseId}
                    entry={entry}
                    previousPassingRun={previousPassingRun}
                  />
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </section>
  );
}

function HistoricalRunResult({
  testCaseId,
  entry,
  previousPassingRun,
}: {
  testCaseId: string;
  entry: ScenarioRunEntry;
  previousPassingRun: WizardCalcSnapshotRun | null;
}) {
  const detailQuery = useWizardCalcSnapshotDetail(testCaseId, entry.result.runResultId);

  if (detailQuery.isLoading) {
    return (
      <div className="text-muted-foreground flex min-h-40 items-center justify-center gap-2">
        <Loader2 className="size-5 animate-spin" />
        Loading historical result…
      </div>
    );
  }

  if (detailQuery.error || !detailQuery.data?.runResult) {
    return (
      <Alert variant="destructive">
        <XCircle />
        <AlertTitle>Unable to load historical result</AlertTitle>
        <AlertDescription>
          {detailQuery.error
            ? getCalcSnapshotErrorMessage(detailQuery.error)
            : 'The saved result is unavailable.'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <CalcSnapshotComparisonPanel
      result={detailQuery.data.runResult}
      expectedSnapshot={
        detailQuery.data.runExpectedSnapshot ?? detailQuery.data.testCase.expectedSnapshot
      }
      testCase={detailQuery.data.testCase}
      currentRun={entry.run}
      previousPassingRun={previousPassingRun}
      context="historical"
      isRunning={false}
      onRun={() => undefined}
    />
  );
}

function findPreviousCleanRun(
  entries: ScenarioRunEntry[],
  activeEntry: ScenarioRunEntry | undefined,
): ScenarioRunEntry | undefined {
  if (!activeEntry) return undefined;
  const activeTime = new Date(activeEntry.result.createdDateTime).getTime();
  return entries.find(
    entry =>
      entry.result.runResultId !== activeEntry.result.runResultId &&
      entry.result.passed &&
      entry.result.warningCount === 0 &&
      entry.result.testCaseRevision === activeEntry.result.testCaseRevision &&
      new Date(entry.result.createdDateTime).getTime() < activeTime,
  );
}

function RevisionHistory({
  revisions,
}: {
  revisions: Array<{
    revisionId: string;
    revision: number;
    reason: string;
    changedBy: string;
    createdDateTime: string;
  }>;
}) {
  if (revisions.length === 0) {
    return (
      <div className="text-muted-foreground rounded-xl border border-dashed p-8 text-center text-sm">
        No revisions have been made since this test case was pinned.
      </div>
    );
  }

  return (
    <ol className="relative ml-3 border-l border-gray-200">
      {revisions.map(revision => (
        <li
          key={revision.revisionId}
          className="relative pb-8 pl-7 last:pb-0"
        >
          <span className="border-primary absolute -left-2 top-0 size-4 rounded-full border-4 bg-white" />
          <div className="rounded-xl border p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium text-gray-950">Revision {revision.revision}</p>
              <span className="text-muted-foreground text-xs">
                {formatSnapshotDateTime(revision.createdDateTime)}
              </span>
            </div>
            <p className="mt-2 text-sm text-gray-700">{revision.reason}</p>
            <p className="text-muted-foreground mt-2 text-xs">Changed by {revision.changedBy}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function RevisionActionDialog({
  mode,
  testCaseId,
  onOpenChange,
  onRevised,
}: {
  mode: 'bless' | 'repin' | null;
  testCaseId: string | null;
  onOpenChange: (open: boolean) => void;
  onRevised: () => void;
}) {
  const [reason, setReason] = useState('');
  const mutation = useReviseWizardCalcSnapshotTestCase(testCaseId);

  useEffect(() => {
    if (!mode) {
      setReason('');
      mutation.reset();
    }
  }, [mode]);

  const submit = () => {
    if (!mode) return;
    mutation.mutate(
      { mode, reason },
      {
        onSuccess: onRevised,
      },
    );
  };

  return (
    <Dialog
      open={mode != null}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'bless' ? 'Accept current results' : 'Update frozen inputs'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'bless'
              ? 'Accept the current calculation output as the new expected snapshot.'
              : 'Freeze the latest source inputs and calculation output as a new revision.'}
          </DialogDescription>
        </DialogHeader>
        <label className="space-y-2 text-sm font-medium text-gray-800">
          <span>Reason</span>
          <Textarea
            value={reason}
            onChange={event => setReason(event.target.value)}
            placeholder="Required reason"
            className="min-h-24 resize-none"
          />
        </label>
        {mutation.error && (
          <p className="text-sm text-red-700">{getCalcSnapshotErrorMessage(mutation.error)}</p>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!reason.trim() || mutation.isPending}
          >
            {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
            {mode === 'bless' ? 'Accept current results' : 'Update frozen inputs'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestCaseStatusBadges({
  testCase,
}: {
  testCase: NonNullable<ReturnType<typeof useWizardCalcSnapshotDetail>['data']>['testCase'];
}) {
  return (
    <>
      <Badge variant="outline">{testCase.strategyId}</Badge>
      <Badge variant="secondary">as of {testCase.asOfDate}</Badge>
      <Badge variant="secondary">revision {testCase.revision}</Badge>
    </>
  );
}

export function ScenarioBadges({
  facets,
}: {
  facets: NonNullable<ReturnType<typeof useWizardCalcSnapshotDetail>['data']>['testCase']['facets'];
}) {
  const badges = [
    facets.isJoint ? 'Joint' : 'Single life',
    facets.isIndemnity ? 'Indemnity' : 'Reimbursement',
    facets.isUnlimited ? 'Unlimited' : null,
    facets.hasExtensionOfBenefits ? 'Extension of benefits' : null,
    facets.hasPriorUtilization ? 'Prior utilization' : null,
    facets.hasStartedLtc ? 'Already on claim' : null,
    `${facets.adlAssistanceCount} ADLs`,
    facets.inflationRate > 0
      ? facets.inflationIsSimple
        ? 'Simple inflation'
        : 'Compound inflation'
      : 'No inflation',
    ...facets.waitingPeriodDays.map(days => `${days}-day wait`),
  ].filter((value): value is string => value != null);

  return (
    <>
      {badges.map(label => (
        <Badge
          key={label}
          variant="outline"
        >
          {label}
        </Badge>
      ))}
    </>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex min-w-0 gap-3">
      <span className="text-muted-foreground mt-0.5 [&>svg]:size-4">{icon}</span>
      <div className="min-w-0">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="mt-0.5 break-words text-sm font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">{title}</h3>
  );
}

function formatComparisonValue(value: unknown, side: 'expected' | 'actual'): string {
  if (value === undefined) return side === 'expected' ? 'Not tracked' : 'Not provided';
  if (value === null) return 'Not available';
  if (typeof value === 'number')
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 9 }).format(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
